import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAdSense } from '../context/AdSenseContext';

/**
 * Google AdSense display unit. Slot IDs are optional — Auto ads can still fill
 * reserved space when the publisher snippet is loaded. Units remount on route
 * change so SPA navigation requests a fresh ad.
 */
export default function AdSenseUnit({
  slot,
  format = 'auto',
  layout,
  layoutKey,
  minHeight = 250,
  className = '',
  label = 'Advertisement',
}) {
  const { enabled, client } = useAdSense();
  const location = useLocation();
  const insRef = useRef(null);
  const resolvedSlot = String(slot || '').trim();

  useEffect(() => {
    if (!enabled || !client) return undefined;

    const timer = window.setTimeout(() => {
      try {
        const node = insRef.current;
        if (!node) return;
        const status = node.getAttribute('data-adsbygoogle-status');
        if (status === 'done' || status === 'filled') return;
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {
        /* AdSense may throw if the script is blocked */
      }
    }, 80);

    return () => window.clearTimeout(timer);
  }, [enabled, client, resolvedSlot, format, location.pathname]);

  if (!enabled || !client) return null;

  return (
    <div
      className={`w-full overflow-hidden rounded-xl border border-stone-200/80 dark:border-stone-700/80 bg-stone-50/80 dark:bg-stone-900/50 ${className}`}
      aria-label={label}
    >
      <p className="px-3 pt-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-400 dark:text-stone-500">
        {label}
      </p>
      <div className="px-2 pb-2">
        <ins
          ref={insRef}
          key={`${location.pathname}:${resolvedSlot}:${format}:${layout || ''}`}
          className="adsbygoogle"
          style={{ display: 'block', minHeight }}
          data-ad-client={client}
          {...(resolvedSlot ? { 'data-ad-slot': resolvedSlot } : {})}
          data-ad-format={format}
          {...(layout ? { 'data-ad-layout': layout } : {})}
          {...(layoutKey ? { 'data-ad-layout-key': layoutKey } : {})}
          data-full-width-responsive="true"
        />
      </div>
    </div>
  );
}

export function AdSenseSidebar({ slot, className = '' }) {
  return (
    <div className={`lg:sticky lg:top-24 space-y-4 ${className}`}>
      <AdSenseUnit
        slot={slot}
        format="auto"
        minHeight={280}
        label="Sponsored"
      />
    </div>
  );
}
